// Configuración centralizada de la aplicación
// Manufacturas Madelmold S.A. - Demo en Plesk
// Adaptado de Athenea MES by Try! Company

// Servidor de demo (Plesk subdomain)
export const SERVER_IP = "madelmold.trycompany.eu";
export const SERVER_PROTOCOL = "https";

// Presets disponibles en la pantalla de configuración
export const SERVER_PRESETS = [
  { id: "demo", label: "Demo Plesk", url: "https://madelmold.trycompany.eu" },
  { id: "local", label: "Desarrollo local", url: "http://localhost:8085" },
];

// Detectar si estamos en Capacitor (app nativa Android)
export const isCapacitor = typeof window !== "undefined" &&
  (window as any).Capacitor !== undefined;

// Detectar si estamos en modo desarrollo
export const isDevelopment = import.meta.env.DEV;

// Override por usuario: localStorage
export const getServerUrlOverride = (): string | null => {
  if (typeof window === "undefined" || !window.localStorage) return null;
  const v = window.localStorage.getItem("madelmold_server_url");
  return v && v.trim() ? v.replace(/\/+$/, "") : null;
};

export const setServerUrlOverride = (url: string | null) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  if (url && url.trim()) {
    window.localStorage.setItem("madelmold_server_url", url.trim().replace(/\/+$/, ""));
  } else {
    window.localStorage.removeItem("madelmold_server_url");
  }
};

// Probar si un endpoint responde a /version.json
export const tryEndpoint = async (url: string, timeoutMs = 4000): Promise<boolean> => {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    const r = await fetch(`${url.replace(/\/+$/, "")}/version.json`, {
      signal: ctrl.signal,
      cache: "no-store",
    });
    clearTimeout(t);
    return r.ok;
  } catch {
    return false;
  }
};

// URLs base según el entorno
export const getBaseUrl = () => {
  if (isCapacitor) {
    return getServerUrlOverride() || `${SERVER_PROTOCOL}://${SERVER_IP}`;
  }
  return "";
};

export const getApiUrl = () => {
  if (isCapacitor) {
    const override = getServerUrlOverride();
    if (override) return `${override}/api`;
    return `${SERVER_PROTOCOL}://${SERVER_IP}/api`;
  }
  return "/api";
};

// Auto-detección de servidor
export const autodetectServer = async (timeoutMs = 3000): Promise<boolean> => {
  if (!isCapacitor) return false;
  const current = getBaseUrl();
  if (await tryEndpoint(current, timeoutMs)) return false;
  for (const p of SERVER_PRESETS) {
    if (p.url === current) continue;
    if (await tryEndpoint(p.url, timeoutMs)) {
      setServerUrlOverride(p.url);
      return true;
    }
  }
  return false;
};

// Configuración de la aplicación - MADELMOLD
export const APP_CONFIG = {
  name: "Madelmold MES",
  version: "1.0.0-demo",
  versionCode: 1,
  companyName: "Manufacturas Madelmold S.A.",
};

// Información de la empresa
export const COMPANY_INFO = {
  legalName: "Manufacturas Madelmold S.A.",
  cif: "A82912924",
  address: "C/ Mario Vargas Llosa, 6",
  industrialPark: "Pol. Ind. Casablanca",
  postalCode: "28850",
  city: "Torrejón de Ardoz",
  province: "Madrid",
  country: "España",
  phone: "918 833 171",
  email: "madelmold@madelmold.net",
  website: "https://madelmold.net",
  founded: 2001,
  employees: "10-50",
  certifications: ["ISO 9001", "ISO 14001"],
};

// Configuración de timeouts
export const TIMEOUTS = {
  apiRequest: 10000,
  loginRequest: 5000,
  updateCheck: 5000,
};

// Módulos habilitados
export const ENABLED_MODULES = {
  dashboard: true,
  workOrders: true,
  tasks: true,
  operator: true,
  molds: true,
  machines: true,
  materials: true,
  qualityControl: true,
  traceability: true,
  recipes: true,
  customers: true,
  suppliers: true,
  billing: true,
  purchaseOrders: true,
  budgets: true,
  personal: true,
  shifts: true,
  fichaje: true,
  compliance: true,
  autoclave: false,
  freezer: false,
  plybook: false,
  plotterCuts: false,
  curingRecipes: false,
};
